/**
 * Wiring interactions and clocks into objects' behavior graphs, as one undo
 * step per call. Each call is applied whole or not at all: everything is
 * checked, against the input and then the scene, before anything changes.
 *
 * The graphs are written with `addBehaviors` (an object with no graph yet)
 * and `commitBehaviors` (one with a graph), so they get those operations'
 * checks and events. A graph on an object inside a placed reference copies
 * the reference first, in the same step, and is compiled after the copy:
 * a graph addresses its targets by paths relative to its object.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { BehaviorGraphData, GraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { BehaviorGraph } from '../../behaviors/behaviorgraph/behaviorgraph';
import type { BehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { GraphBuilder } from '../../behaviors/behaviorgraph/GraphBuilder';
import { Properties } from '../../behaviors/properties';
import type { UndoCommand } from '../undo/UndoCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { addBehaviors, commitBehaviors, type BehaviorAddEntry, type BehaviorCommitEntry } from './behaviorOps';
import { assertAuthoredGraph } from './behaviorInput';
import { editRefusal, refuse, sceneOf } from './checks';
import { checkInteraction, compileChecked, composeInteraction } from './interactionGraph';
import type { GraphUpdate, Interaction, InteractionEvent } from './interactionGraph';
import { copyForInsideEdit } from './referenceEdit';
import { bundle, rollBack } from './steps';
import type { AuthoringPorts, OpResult, Refusal } from './types';

/** An interaction as wired: its object (after any copy), what each action does, and the change to the object's graph. */
export interface WiredInteraction {
    object: SceneObject;
    event: InteractionEvent;
    summaries: string[];
    update: GraphUpdate;
}

/**
 * Wire each interaction into its object's graph, in order, as one undo step.
 * Several interactions on one object build on each other; `replace` is only
 * allowed on an object's first.
 *
 * Throws, before anything changes and in any mode, on an interaction that is
 * malformed on its own (see `checkInteraction`). Then refuses play mode, an
 * undo in progress and a deleted object. Then, against the scene, refuses an
 * action it can't carry out (`INVALID_WIRING`, see `compileInteraction`) and
 * an event an object's graph already handles, when appending
 * (`EVENT_CONFLICT`), naming the interaction and action in the message; and
 * throws on `replace` after an object's first interaction, on an expression
 * that doesn't compile, and on a graph that can't run once the interaction
 * is added. Then refuses a copy the reference rule refuses. Asked again once
 * the graphs are written: if play started, an undo began, or an object,
 * scope or target was deleted meanwhile, everything is taken back and
 * refused. A fault while writing throws after everything is taken back.
 */
export async function wireInteractions(
    interactions: readonly Interaction[],
    ports: AuthoringPorts = {},
): Promise<OpResult<{ wired: WiredInteraction[]; orphaned: string[] }>> {
    if (interactions.length === 0) throw new Error('[wireInteractions] needs at least one interaction');
    for (const [index, interaction] of interactions.entries()) {
        try {
            checkInteraction(interaction);
        } catch (error) {
            throw new Error(`interaction ${index + 1}: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    const objects = [...new Set(interactions.map(interaction => interaction.object))];
    const scene = sceneOf(interactions.flatMap(involved));
    const policy = editRefusal(scene, interactions.flatMap(involved));
    if (policy) return { refused: policy };
    const first = stageInteractions(interactions);
    if ('refused' in first) return first;

    return writeGraphs(objects, ports, `Wire ${interactions.length} interactions`, copied => {
        if (!copied) return first;
        const moved = (object: SceneObject): SceneObject => copied.get(object) ?? object;
        return stageInteractions(interactions.map(interaction => ({
            ...interaction,
            object: moved(interaction.object),
            ...(interaction.scope ? { scope: moved(interaction.scope) } : {}),
            actions: interaction.actions.map(action => ('target' in action ? { ...action, target: moved(action.target) } : action)),
        })));
    }, staged => ({ wired: staged.wired }));
}

/** A periodic clock on an object's own numeric property. */
export interface Clock {
    /** The phase: an own `number` property without units. */
    property: string;
    /** The phase wraps into [0, period). Positive. */
    period: number;
    /** Units per second, before `rateProperties`. Default 1. */
    rateScale?: number;
    /** Own unitless `number` properties the rate is multiplied by, read every frame. */
    rateProperties?: readonly string[];
    /** An own `boolean` property; while it is false the clock holds its value. */
    runningProperty?: string;
}

/**
 * Wire a clock into the object's graph, as one undo step: every frame,
 * `property += deltaTime × rateScale × rateProperties…`, wrapped into
 * [0, period), unless `runningProperty` is false. A change of rate keeps the
 * phase, and a value set while the clock is stopped holds. The tick event is
 * appended to the object's graph, keeping its other events.
 *
 * Throws, before anything changes, on a period that isn't positive and
 * finite, a rate scale that isn't finite, a property name that isn't a
 * non-empty string, and a phase that is also a rate. Refuses as
 * {@link wireInteractions} does; `INVALID_WIRING` is a property the object
 * doesn't declare itself with the type the clock needs, no units and a finite
 * value now, and `EVENT_CONFLICT` a graph that already handles ticks.
 */
export async function wireClock(
    object: SceneObject,
    clock: Clock,
    ports: AuthoringPorts = {},
): Promise<OpResult<{ update: GraphUpdate; orphaned: string[] }>> {
    checkClock(clock);
    const policy = editRefusal(object.traceScene, [object]);
    if (policy) return { refused: policy };
    const first = stageClock(object, clock);
    if ('refused' in first) return first;
    return writeGraphs([object], ports, `Wire clock on ${object.name}`, copied => (copied ? stageClock(object, clock) : first),
        staged => ({ update: staged.wired[0].update }));
}

// ── Staging ──────────────────────────────────────────────────────────────────

/** The graph data to write on each object, and what each interaction did. */
interface Staged {
    graphs: Map<SceneObject, BehaviorGraphData>;
    wired: WiredInteraction[];
    /** Every object the graphs name, which must still stand when they are written. */
    involved: SceneObject[];
}

/**
 * Compile each interaction and fold it into its object's graph, changing
 * nothing. Throws, naming the interaction, when a folded graph can't run (an
 * existing graph that is already broken, say).
 */
function stageInteractions(interactions: readonly Interaction[]): Staged | { refused: Refusal } {
    const graphs = new Map<SceneObject, BehaviorGraphData>();
    const wired: WiredInteraction[] = [];
    for (const [index, interaction] of interactions.entries()) {
        const at = (text: string) => `interaction ${index + 1}: ${text}`;
        const { object } = interaction;
        let compiled, composed;
        try {
            // A later replace would erase what the batch's earlier interactions wired.
            if (graphs.has(object) && interaction.mode === 'replace') throw new Error('mode "replace" must be the first interaction for an object');
            compiled = compileChecked(interaction);
            if ('refused' in compiled) return { refused: { ...compiled.refused, message: at(compiled.refused.message) } };
            composed = compose(object, graphs, compiled.graph, interaction.mode);
            if (!('refused' in composed)) assertAuthoredGraph(graphs.get(object)!);
        } catch (error) {
            throw new Error(at(error instanceof Error ? error.message : String(error)));
        }
        if ('refused' in composed) {
            const remedy = 'pass mode "replace" to replace the whole graph, or edit the graph to run several actions on one event';
            return { refused: { ...composed.refused, message: at(`${composed.refused.message}: ${remedy}`) } };
        }
        wired.push({ object, event: interaction.event ?? 'select', summaries: compiled.summaries, update: composed.update });
    }
    return { graphs, wired, involved: interactions.flatMap(involved) };
}

/** The graph for a clock: every frame, `property += deltaTime x rate`, wrapped into [0, period). The object's Properties are not checked here. */
export function clockGraph(object: SceneObject, clock: Clock): GraphData {
    const rates = clock.rateProperties ?? [];
    const running = clock.runningProperty;
    const builder = new GraphBuilder(object);
    const tick = builder._addNode('event/onTick', {});
    const get = (name: string) => builder.addGetPropertyNode(object, name);
    const math = (op: string, node: number, socket: string, right: number | { node: number; socket: string }) => {
        const result = builder._addNode(op, {});
        builder._setValueRef(result, 'a', node, socket);
        if (typeof right === 'number') builder._setValue(result, 'b', right);
        else builder._setValueRef(result, 'b', right.node, right.socket);
        return result;
    };
    let delta = math('math/mul', tick, 'deltaTime', clock.rateScale ?? 1);
    for (const name of rates) delta = math('math/mul', delta, 'value', { node: get(name), socket: 'value' });
    const sum = math('math/add', get(clock.property), 'value', { node: delta, socket: 'value' });
    const turns = builder.addFloorNode(math('math/div', sum, 'value', clock.period));
    const wrapped = math('math/sub', sum, 'value', { node: math('math/mul', turns, 'value', clock.period), socket: 'value' });
    const flow = builder.flowFrom(tick, 'out');
    (running ? flow.branch(get(running)).onTrue() : flow).setPropertyFrom(object, clock.property, wrapped);
    return builder.build();
}

function stageClock(object: SceneObject, clock: Clock): Staged | { refused: Refusal } {
    const props = object.getBehavior(Properties);
    if (!props) return { refused: refuse('INVALID_WIRING', `"${object.name}" has no Properties; declare the clock's properties first`, object) };
    const declared = (name: string, type: 'number' | 'boolean'): Refusal | null => {
        const own = props.getData().properties?.find(property => property.name === name);
        if (!own || own.type !== type || (own.type === 'number' && own.units)) {
            return refuse('INVALID_WIRING', `"${name}" must be an own ${type} property${type === 'number' ? ' without units' : ''}`, object);
        }
        const value = props.getValue(name);
        if (typeof value !== type || (type === 'number' && !Number.isFinite(value))) {
            return refuse('INVALID_WIRING', `"${name}" must currently resolve to a finite ${type} value`, object);
        }
        return null;
    };
    const rates = clock.rateProperties ?? [];
    for (const name of [clock.property, ...rates]) {
        const refusal = declared(name, 'number');
        if (refusal) return { refused: refusal };
    }
    const running = clock.runningProperty;
    const notRunning = running === undefined ? null : declared(running, 'boolean');
    if (notRunning) return { refused: notRunning };

    const graphs = new Map<SceneObject, BehaviorGraphData>();
    const composed = compose(object, graphs, clockGraph(object, clock), 'append');
    if ('refused' in composed) return composed;
    assertAuthoredGraph(graphs.get(object)!);
    return { graphs, wired: [{ object, event: 'tick', summaries: [], update: composed.update }], involved: [object] };
}

/** Fold `graph` into what `object`'s graph is staged to become, or is now. */
function compose(
    object: SceneObject,
    graphs: Map<SceneObject, BehaviorGraphData>,
    graph: GraphData,
    mode: 'append' | 'replace' | undefined,
): { update: GraphUpdate } | { refused: Refusal } {
    const current = graphs.get(object) ?? object.getBehavior(BehaviorGraph)?.getData();
    const composed = composeInteraction(current, graph, mode);
    if ('conflicts' in composed) {
        return { refused: refuse(
            'EVENT_CONFLICT',
            `"${object.name}" already handles ${composed.conflicts.join(', ')} (its graph handles ${composed.existingEvents.join(', ')}), `
                + 'so its graph is unchanged',
            object,
        ) };
    }
    graphs.set(object, composed.data);
    return { update: composed.update };
}

function checkClock(clock: Clock): void {
    if (typeof clock.period !== 'number' || !Number.isFinite(clock.period) || clock.period <= 0) throw new Error('period must be finite and positive');
    const scale = clock.rateScale ?? 1;
    if (typeof scale !== 'number' || !Number.isFinite(scale)) throw new Error('the rate scale must be finite');
    const rates: unknown = clock.rateProperties ?? [];
    if (!Array.isArray(rates)) throw new Error('the rate properties must be a list of property names');
    const names: unknown[] = [clock.property, ...(rates as unknown[])];
    if (clock.runningProperty !== undefined) names.push(clock.runningProperty);
    for (const name of names) {
        if (typeof name !== 'string' || !name) throw new Error(`Expected a property name, got ${JSON.stringify(name)}`);
    }
    if ((rates as unknown[]).includes(clock.property)) throw new Error('Clock phase cannot also drive its own rate');
}

/** Every object an interaction names. */
function involved(interaction: Interaction): SceneObject[] {
    return [
        interaction.object,
        ...(interaction.scope ? [interaction.scope] : []),
        ...interaction.actions.flatMap(action => ('target' in action ? [action.target] : [])),
    ];
}

// ── Writing ──────────────────────────────────────────────────────────────────

// A batch entry's constructor takes the widest data, which a concrete class's doesn't.
const GRAPH = BehaviorGraph as unknown as BehaviorConstructor;

/**
 * Copy the references `objects` sit inside, stage again if that moved
 * anything (`stage` is handed what the copy replaced), and write each staged
 * graph: an add where the object has none, a commit where it has one. One
 * undo step for all of it; any refusal or fault takes everything back.
 */
async function writeGraphs<T>(
    objects: readonly SceneObject[],
    ports: AuthoringPorts,
    label: string,
    stage: (copied: Map<SceneObject, SceneObject> | null) => Staged | { refused: Refusal },
    result: (staged: Staged) => T,
): Promise<OpResult<T & { orphaned: string[] }>> {
    const copy = await copyForInsideEdit(objects, ports);
    if ('refused' in copy) return copy;
    const steps: UndoCommand[] = copy.command ? [copy.command] : [];
    // What the two ops announced, to correct when this takes them back: their undo is silent.
    let added: SceneObject[] = [], committed: SceneObject[] = [];
    // Takes each step back once, even when a refusal's take-back throws into the `catch`.
    const takeBack = async (cause: unknown): Promise<void> => {
        // Paths as announced: an object a copy left may be gone once the copy is taken back.
        const told = [
            ...added.map(object => ({ path: object.getPath(), action: 'removed' as const })),
            ...committed.map(object => ({ path: object.getPath(), action: 'updated' as const })),
        ];
        added = [];
        committed = [];
        try {
            await rollBack(steps.splice(0), cause);
        } finally {
            for (const { path, action } of told) {
                dispatchBehaviorChanged({ objectPath: path, behaviorType: BehaviorGraph.behaviorType, action });
            }
        }
    };
    try {
        // Asked again: the scene may have changed while the copy uploaded.
        const staged = stage(copy.command ? copy.copied : null);
        if ('refused' in staged) {
            await takeBack(staged.refused);
            return staged;
        }
        const gone = editRefusal(objects[0].traceScene, staged.involved);
        if (gone) {
            await takeBack(gone);
            return { refused: gone };
        }
        const adds: BehaviorAddEntry[] = [];
        const commits: BehaviorCommitEntry[] = [];
        for (const [object, data] of staged.graphs) {
            const behavior = object.getBehavior(BehaviorGraph);
            if (behavior) commits.push({ behavior, patch: data });
            else adds.push({ object, type: GRAPH, data });
        }
        const addResult = await addBehaviors(adds, ports);
        if (addResult.command) steps.push(addResult.command);
        added = addResult.applied.map(behavior => behavior.sceneObject);
        const commitResult = addResult.refused.length > 0 ? null : await commitBehaviors(commits, ports);
        if (commitResult?.command) steps.push(commitResult.command);
        committed = commitResult?.applied.map(behavior => behavior.sceneObject) ?? [];
        const late = addResult.refused[0] ?? commitResult?.refused[0];
        if (late) {
            // Its `entry` indexes this function's adds or commits, not the caller's batch.
            const { entry: _entry, ...refused } = late;
            await takeBack(refused);
            return { refused };
        }
        // Those ops ask again about the objects they wrote; a target or scope may have gone too.
        const lost = editRefusal(objects[0].traceScene, staged.involved);
        if (lost) {
            await takeBack(lost);
            return { refused: lost };
        }
        return { ...result(staged), orphaned: copy.orphaned, command: bundle(label, steps) };
    } catch (error) {
        await takeBack(error);
        throw error;
    }
}
